import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, ListItemText, ListItemIcon, Divider, Typography, Chip } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ClearIcon from '@mui/icons-material/Clear';
import { format } from 'date-fns';
import { useNavigationHistory } from '../hooks/useNavigationHistory';

const NavigationHistory: React.FC = () => {
  const { history, canGoBack, canGoForward, goBack, goForward, clearHistory, goToHistoryIndex } = useNavigationHistory();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (index: number) => {
    goToHistoryIndex(index);
    handleClose();
  };

  const handleClearHistory = () => {
    clearHistory();
    handleClose();
  };

  const getEntityTypeColor = (type?: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (type) {
      case 'stores': return 'primary';
      case 'workers': return 'secondary';
      case 'plans': return 'success';
      case 'programs': return 'info';
      case 'chains': return 'warning';
      default: return 'default';
    }
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={goBack}
        disabled={!canGoBack}
        title="Go Back"
        style={{ marginRight: 4 }}
      >
        <ArrowBackIcon />
      </IconButton>
      
      <IconButton
        size="small"
        onClick={goForward}
        disabled={!canGoForward}
        title="Go Forward"
        style={{ marginRight: 8 }}
      >
        <ArrowForwardIcon />
      </IconButton>
      
      <IconButton
        size="small"
        onClick={handleClick}
        title="View History"
      >
        <HistoryIcon />
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: 400,
            width: 350,
          },
        }}
      >
        <MenuItem disabled style={{ opacity: 1 }}>
          <Typography variant="subtitle2" color="textSecondary">
            Navigation History
          </Typography>
        </MenuItem>
        <Divider />
        
        {history.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary="No history yet" />
          </MenuItem>
        ) : (
          <>
            {history.slice().reverse().map((item, reverseIndex) => {
              const actualIndex = history.length - 1 - reverseIndex;
              return (
                <MenuItem
                  key={actualIndex}
                  onClick={() => handleNavigate(actualIndex)}
                  style={{ paddingTop: 8, paddingBottom: 8 }}
                >
                  <ListItemText
                    primary={
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{item.title}</span>
                        {item.entityType && (
                          <Chip
                            label={item.entityType}
                            size="small"
                            color={getEntityTypeColor(item.entityType)}
                            variant="outlined"
                          />
                        )}
                      </div>
                    }
                    secondary={format(new Date(item.timestamp), 'HH:mm:ss')}
                  />
                </MenuItem>
              );
            })}
            
            <Divider />
            <MenuItem onClick={handleClearHistory}>
              <ListItemIcon>
                <ClearIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Clear History" />
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default NavigationHistory;